import { aiDecisionSeed, type Difficulty, type Personality } from '../ai';
import {
  applyAction,
  cardPlayability,
  epitaphOptions,
  legalActions,
  makeReplay,
  previewLink,
  redactEvents,
  runReplay,
  startMatch,
  view,
  type Action,
  type EngineError,
  type EpitaphOption,
  type LinkPreview,
  type MatchConfig,
  type MatchEvent,
  type MatchState,
  type MatchView,
  type Playability,
  type PlayLinkAction,
  type Replay,
  type Side,
} from '../engine';
import type { AiClient } from './aiClient';

/**
 * The local match host: it owns the full state and is the only place that calls applyAction.
 * Human seats act through submit(); computer seats are asked through the AI client, which only
 * receives each side's view (spec §14, §20.2). The UI renders snapshots, never the raw state.
 */

export type Controller =
  | { readonly kind: 'human'; readonly name: string }
  | {
      readonly kind: 'ai';
      readonly name: string;
      readonly difficulty: Difficulty;
      readonly personality?: Personality;
    };

export interface MatchSetup {
  readonly config: MatchConfig;
  readonly seed: string;
  /** Controllers keyed by seating player id (config.seating.players). */
  readonly controllers: Readonly<Record<string, Controller>>;
  /** Identity of the match for match logging (src/app/matchLog.ts), kept when it is resumed. */
  readonly id?: string;
  /** When the match was started (ISO 8601). */
  readonly startedAt?: string;
}

export interface HostSnapshot {
  readonly version: number;
  /** Number of actions applied; changes only when the state changes. */
  readonly actionCount: number;
  readonly viewer: Side;
  readonly view: MatchView;
  readonly controllers: Readonly<Record<Side, Controller>>;
  /** Every event so far, redacted for the viewer. */
  readonly log: readonly MatchEvent[];
  /** Events of the most recent action, redacted for the viewer (animation and sound cues). */
  readonly lastEvents: readonly MatchEvent[];
  readonly lastActor: Side | null;
  /** Side whose computer player is deciding. */
  readonly thinking: Side | null;
  /** Two-player games: the side that must take the device before its hand is shown. */
  readonly handoff: Side | null;
  readonly error: string | null;
  /** A turn sent with submitAll is still on its way (online: the table has not applied it all). */
  readonly busy: boolean;
}

export interface HostOptions {
  readonly ai: AiClient;
  /** Actions already played (resuming a saved match). */
  readonly actions?: readonly Action[];
  /**
   * A position to start from instead of the start of the match (the tutorial's lessons). Such a
   * match has no replay; `actions` is ignored.
   */
  readonly initialState?: MatchState;
  /** Pause after each computer action, in ms. */
  readonly delay?: (action?: Action) => number;
  /** Called after every applied action, e.g. to save progress or record telemetry. */
  readonly onAction?: (
    before: MatchState,
    action: Action,
    after: MatchState,
    events: readonly MatchEvent[],
  ) => void;
  /** Called once when the host is disposed: the match was left or closed. */
  readonly onDispose?: () => void;
}

/**
 * A position reached by actions that have not been sent yet: the turn a player is building,
 * shown at once and undone freely until it is ended. Only actions that reveal nothing hidden
 * are drafted (links and Epitaph replays), so the draft is exact even when the state it starts
 * from was rebuilt from a view (online games).
 */
export interface Draft {
  readonly view: MatchView;
  /** Events of each drafted action, redacted for the viewer. */
  readonly events: readonly (readonly MatchEvent[])[];
  /** For each drafted link, its preview in the position it was played from. */
  readonly previews: readonly (LinkPreview | null)[];
  legal(side: Side): Action[];
  preview(action: PlayLinkAction): LinkPreview | { error: EngineError };
  playability(side: Side, cardId: string): Playability;
  epitaphOptions(side: Side): EpitaphOption[];
}

/** Plays actions on a copy of a state, for the viewer, without committing anything. */
export function draftFrom(
  state: MatchState,
  viewer: Side,
  actions: readonly Action[],
): Draft | { error: EngineError } {
  let s = state;
  const events: MatchEvent[][] = [];
  const previews: (LinkPreview | null)[] = [];
  for (const action of actions) {
    if (action.type === 'PLAY_LINK') {
      const p = previewLink(s, action);
      previews.push('error' in p ? null : p);
    } else previews.push(null);
    const res = applyAction(s, action);
    if (res.error) return { error: res.error };
    s = res.state;
    events.push(redactEvents(res.events, viewer, s.config));
  }
  const final = s;
  let legalCache: { side: Side; actions: Action[] } | null = null;
  return {
    view: view(final, viewer),
    events,
    previews,
    legal: (side) => {
      if (legalCache?.side !== side) legalCache = { side, actions: legalActions(final, side) };
      return legalCache.actions;
    },
    preview: (action) => previewLink(final, action),
    playability: (side, cardId) => cardPlayability(final, side, cardId),
    epitaphOptions: (side) => epitaphOptions(final, side),
  };
}

/**
 * What the match screen needs from a host: the local MatchHost, or RemoteHost for online games
 * (src/app/remoteHost.ts), where the state lives in a room on the server.
 */
export interface GameHost {
  subscribe(listener: () => void): () => void;
  getSnapshot(): HostSnapshot;
  /** Whether the side is played on this device. */
  isHuman(side: Side): boolean;
  legal(side: Side): Action[];
  preview(action: PlayLinkAction): LinkPreview | { error: EngineError };
  playability(side: Side, cardId: string): Playability;
  epitaphOptions(side: Side): EpitaphOption[];
  submit(action: Action): EngineError | null;
  /**
   * Sends a drafted turn: the actions in order, each once the last has been applied (online, as
   * the table answers). Returns the first refusal it can see at once.
   */
  submitAll(actions: readonly Action[]): EngineError | null;
  /** The viewer's actions played on a copy of the current state (see Draft). */
  draft(actions: readonly Action[]): Draft | { error: EngineError };
  setPaused(paused: boolean): void;
  acknowledgeHandoff(): void;
}

/** A safe move for a computer seat whose AI failed: the first legal action other than conceding. */
const fallbackAction = (legal: readonly Action[]): Action =>
  (legal.find((a) => a.type !== 'CONCEDE') ?? legal[0]) as Action;

export class MatchHost implements GameHost {
  private state: MatchState;
  private readonly events: MatchEvent[];
  private readonly actions: Action[];
  private readonly listeners = new Set<() => void>();
  private readonly controllers: Record<Side, Controller>;
  private readonly humans: Side[];
  private readonly fromPosition: boolean;
  private viewer: Side;
  private handoff: Side | null = null;
  private thinking: Side | null = null;
  private lastEvents: MatchEvent[] = [];
  private lastActor: Side | null = null;
  private error: string | null = null;
  private version = 0;
  private generation = 0;
  private disposed = false;
  private paused = false;
  private timer: ReturnType<typeof setTimeout> | null = null;
  private snapshot: HostSnapshot;
  private legalCache: { state: MatchState; side: Side; actions: Action[] } | null = null;

  constructor(
    readonly setup: MatchSetup,
    private readonly options: HostOptions,
  ) {
    this.fromPosition = !!options.initialState;
    if (options.initialState) {
      this.state = options.initialState;
      this.events = [];
      this.actions = [];
    } else if (options.actions && options.actions.length > 0) {
      const run = runReplay(makeReplay(setup.config, setup.seed, options.actions));
      this.state = run.state;
      this.events = [...run.setupEvents, ...run.actionEvents.flat()];
      this.actions = [...options.actions];
    } else {
      const start = startMatch(setup.config, setup.seed);
      this.state = start.state;
      this.events = [...start.events];
      this.actions = [];
    }
    const seatOf = (side: Side): Controller => {
      const c = setup.controllers[this.state.seats[side]];
      if (!c) throw new Error(`no controller for seat ${this.state.seats[side]}`);
      return c;
    };
    this.controllers = { S: seatOf('S'), N: seatOf('N') };
    this.humans = (['S', 'N'] as const).filter((s) => this.controllers[s].kind === 'human');
    this.viewer = this.humans.length === 1 ? (this.humans[0] as Side) : this.state.decider;
    if (this.humans.length === 2 && this.state.phase !== 'MATCH_OVER')
      this.handoff = this.state.decider;
    this.snapshot = this.buildSnapshot();
    queueMicrotask(() => this.pump());
  }

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  getSnapshot = (): HostSnapshot => this.snapshot;

  /** The match as a replay; not available for a match started from a position. */
  get replay(): Replay {
    if (this.fromPosition) throw new Error('a match started from a position has no replay');
    return makeReplay(this.setup.config, this.setup.seed, this.actions);
  }

  get actionCount(): number {
    return this.actions.length;
  }

  /** The full state, for analysis that only reveals the deciding side's own information. */
  get currentState(): MatchState {
    return this.state;
  }

  isHuman(side: Side): boolean {
    return this.controllers[side].kind === 'human';
  }

  /** Legal actions for a side (§16.6), cached per state. */
  legal(side: Side): Action[] {
    const c = this.legalCache;
    if (c && c.state === this.state && c.side === side) return c.actions;
    const actions = legalActions(this.state, side);
    this.legalCache = { state: this.state, side, actions };
    return actions;
  }

  preview(action: PlayLinkAction): LinkPreview | { error: EngineError } {
    return previewLink(this.state, action);
  }

  playability(side: Side, cardId: string): Playability {
    return cardPlayability(this.state, side, cardId);
  }

  epitaphOptions(side: Side): EpitaphOption[] {
    return epitaphOptions(this.state, side);
  }

  draft(actions: readonly Action[]): Draft | { error: EngineError } {
    return draftFrom(this.state, this.viewer, actions);
  }

  /** Human action. Returns the engine error when the action is rejected. */
  submit(action: Action): EngineError | null {
    if (this.disposed) return { code: 'MATCH_OVER', message: 'the match was closed' };
    if (!this.isHuman(action.side))
      return { code: 'NOT_YOUR_DECISION', message: 'that side is not played here' };
    if (action.type !== 'CONCEDE' && this.handoff)
      return { code: 'NOT_YOUR_DECISION', message: 'waiting for the hand-off' };
    const res = applyAction(this.state, action);
    if (res.error) return res.error;
    this.commit(action, res.state, res.events);
    return null;
  }

  submitAll(actions: readonly Action[]): EngineError | null {
    for (const action of actions) {
      const err = this.submit(action);
      if (err) return err;
    }
    return null;
  }

  /** Holds computer moves (e.g. while a round summary is shown). */
  setPaused(paused: boolean): void {
    if (this.paused === paused) return;
    this.paused = paused;
    if (!paused) this.pump();
  }

  acknowledgeHandoff(): void {
    if (!this.handoff) return;
    this.viewer = this.handoff;
    this.handoff = null;
    this.publish();
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    this.generation++;
    if (this.timer) clearTimeout(this.timer);
    this.listeners.clear();
    this.options.onDispose?.();
  }

  private commit(action: Action, next: MatchState, events: MatchEvent[]): void {
    const before = this.state;
    this.state = next;
    this.actions.push(action);
    this.events.push(...events);
    this.lastEvents = events;
    this.lastActor = action.side;
    this.error = null;
    this.generation++;
    this.thinking = null;
    if (this.timer) {
      clearTimeout(this.timer);
      this.timer = null;
    }
    try {
      this.options.onAction?.(before, action, next, events);
    } catch (e) {
      this.error = e instanceof Error ? e.message : String(e);
    }
    this.pump();
  }

  private pump(): void {
    if (this.disposed) return;
    const s = this.state;
    if (s.phase !== 'MATCH_OVER') {
      const decider = s.decider;
      const controller = this.controllers[decider];
      if (controller.kind === 'ai') {
        if (this.thinking !== decider && !this.paused) this.askAi(decider, controller);
      } else if (this.humans.length === 2 && this.viewer !== decider) {
        this.handoff = decider;
      }
    } else {
      this.handoff = null;
    }
    this.publish();
  }

  private askAi(side: Side, controller: Extract<Controller, { kind: 'ai' }>): void {
    const generation = this.generation;
    const state = this.state;
    this.thinking = side;
    const started = Date.now();
    const legal = this.legal(side);
    this.options.ai
      .decide(view(state, side), legal, {
        seed: aiDecisionSeed(state, side),
        difficulty: controller.difficulty,
        ...(controller.personality ? { personality: controller.personality } : {}),
      })
      .catch((e: unknown) => {
        console.error('AI failed; playing a fallback action', e);
        return { action: fallbackAction(legal), reason: 'fallback', nodes: 0 };
      })
      .then((decision) => {
        if (this.disposed || generation !== this.generation) return;
        const wait = Math.max(
          0,
          (this.options.delay?.(decision.action) ?? 0) - (Date.now() - started),
        );
        this.timer = setTimeout(() => {
          this.timer = null;
          if (this.disposed || generation !== this.generation) return;
          if (this.paused) {
            // Resume later from scratch; the decision is deterministic for this state.
            this.thinking = null;
            this.publish();
            return;
          }
          let action = decision.action;
          let res = applyAction(this.state, action);
          if (res.error) {
            // Never leave a computer seat stuck: an illegal choice is replaced by a legal one.
            console.error('AI chose an illegal action; playing a fallback action', res.error);
            action = fallbackAction(legal);
            res = applyAction(this.state, action);
          }
          if (res.error) {
            this.error = `${res.error.code}: ${res.error.message}`;
            this.thinking = null;
            this.publish();
            return;
          }
          this.commit(action, res.state, res.events);
        }, wait);
      });
  }

  private buildSnapshot(): HostSnapshot {
    return {
      version: this.version,
      actionCount: this.actions.length,
      viewer: this.viewer,
      view: view(this.state, this.viewer),
      controllers: this.controllers,
      log: redactEvents(this.events, this.viewer, this.state.config),
      lastEvents: redactEvents(this.lastEvents, this.viewer, this.state.config),
      lastActor: this.lastActor,
      thinking: this.thinking,
      handoff: this.handoff,
      error: this.error,
      busy: false,
    };
  }

  private publish(): void {
    this.version++;
    this.snapshot = this.buildSnapshot();
    for (const l of this.listeners) l();
  }
}
